import { createEnum } from './helpers'

// Pause/Resume Scheduler
export const SCHEDULER_ACTIONS_VALUES = ['Execute', 'Delayed', 'Suspend'] as const
export const SCHEDULER_STATUS_VALUES = ['Active', 'Inactive'] as const

export type SchedulerAction = (typeof SCHEDULER_ACTIONS_VALUES)[number]
export type SchedulerStatus = (typeof SCHEDULER_STATUS_VALUES)[number]

export const SCHEDULER_ACTIONS: Record<string, SchedulerAction> = {
  EXECUTE: 'Execute',
  DELAYED: 'Delayed',
  SUSPEND: 'Suspend',
}

export const SCHEDULER_STATUS: Record<string, SchedulerStatus> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
}

// Blob Transfer
export const BLOB_TRANSFER_ACTIONS_VALUES = ['Execute', 'Delayed', 'Suspend'] as const
export type BlobTransferAction = (typeof BLOB_TRANSFER_ACTIONS_VALUES)[number]
export const BLOB_TRANSFER_ACTIONS: Record<string, BlobTransferAction> = {
  EXECUTE: 'Execute',
  DELAYED: 'Delayed',
  SUSPEND: 'Suspend',
}

export const BLOB_TRANSFER_STATUS_VALUES = ['ACTIVE', 'INACTIVE'] as const
export const BLOB_TRANSFER_STATUS = createEnum(BLOB_TRANSFER_STATUS_VALUES)
export type BlobTransferStatus = (typeof BLOB_TRANSFER_STATUS_VALUES)[number]

// Lambda Control Status
export const LAMBDA_CONTROL_STATUS_VALUES = ['ACTIVE', 'INACTIVE'] as const
export const LAMBDA_CONTROL_STATUS = createEnum(LAMBDA_CONTROL_STATUS_VALUES)
export type LambdaControlStatus = (typeof LAMBDA_CONTROL_STATUS_VALUES)[number]

// Sync Use Case IDs
export const SYNC_USE_CASE_ID_VALUES = [
  'SFAccount',
  'OrganizationHierarchy',
  'Material',
  'Resource',
] as const
export const SYNC_USE_CASE_IDS = createEnum(SYNC_USE_CASE_ID_VALUES)
export type SyncUseCaseId = (typeof SYNC_USE_CASE_ID_VALUES)[number]

// Sync Downstream Names
export const SYNC_DOWNSTREAM_NAME_VALUES = ['SAP', 'NOD', 'SALESFORCE', 'NEXT'] as const
export const SYNC_DOWNSTREAM_NAMES = createEnum(SYNC_DOWNSTREAM_NAME_VALUES)
export type SyncDownstreamName = (typeof SYNC_DOWNSTREAM_NAME_VALUES)[number]

// Sync Account SAP Transaction Types
/**
 * Offered on the Nova line alone. It stays in the union below so a Nova request validates against
 * the shared zod schema; the modal filters it out where `isNova` is false, the same way the SAP
 * order source is handled.
 */
export const SYNC_ACCOUNT_SAP_TRANSACTION_TYPE_NOVA_ONLY = ['Z001'] as const

export const SYNC_ACCOUNT_SAP_TRANSACTION_TYPE_VALUES = [
  'Z003',
  '0012',
  ...SYNC_ACCOUNT_SAP_TRANSACTION_TYPE_NOVA_ONLY,
] as const
export const SYNC_ACCOUNT_SAP_TRANSACTION_TYPES = createEnum(
  SYNC_ACCOUNT_SAP_TRANSACTION_TYPE_VALUES,
)
export type SyncAccountSapTransactionType =
  (typeof SYNC_ACCOUNT_SAP_TRANSACTION_TYPE_VALUES)[number]

// Sync Material specific constants
/** Offered on the Nova line alone; see `SYNC_ACCOUNT_SAP_TRANSACTION_TYPE_NOVA_ONLY`. */
export const SYNC_MATERIAL_DOWNSTREAM_NAME_NOVA_ONLY = ['RYDER'] as const

export const SYNC_MATERIAL_DOWNSTREAM_NAME_VALUES = [
  'SALESFORCE',
  'NOD',
  'NEXT',
  ...SYNC_MATERIAL_DOWNSTREAM_NAME_NOVA_ONLY,
] as const
export const SYNC_MATERIAL_DOWNSTREAM_NAMES = createEnum(SYNC_MATERIAL_DOWNSTREAM_NAME_VALUES)
export type SyncMaterialDownstreamName = (typeof SYNC_MATERIAL_DOWNSTREAM_NAME_VALUES)[number]

export const SYNC_MATERIAL_TYPE_VALUES = ['Merchandise', 'SKU'] as const
export const SYNC_MATERIAL_TYPES = createEnum(SYNC_MATERIAL_TYPE_VALUES)
export type SyncMaterialType = (typeof SYNC_MATERIAL_TYPE_VALUES)[number]

export const SYNC_MATERIAL_OPERATION_VALUES = ['material', 'DMTV'] as const
export const SYNC_MATERIAL_OPERATIONS = createEnum(SYNC_MATERIAL_OPERATION_VALUES)
export type SyncMaterialOperation = (typeof SYNC_MATERIAL_OPERATION_VALUES)[number]

// Operation Endpoints
export const OPERATION_ENDPOINT_VALUES = [
  '/sales-order/edi-reprocess',
  '/soLambdaControl',
  '/resend',
  '/schedule/transfer_pause_resume',
  '/plecc/process',
  '/asor/invoke_bcp_job',
  '/sync',
  '/sales-order/mark-dead-failed-orders',
] as const
export const OPERATION_ENDPOINTS = createEnum(OPERATION_ENDPOINT_VALUES)
export type OperationEndpoint = (typeof OPERATION_ENDPOINT_VALUES)[number]

// Sync Operation Types
export const SYNC_OPERATION_TYPE_VALUES = [
  'syncAccountSap',
  'syncAccountNod',
  'syncOrgHier',
  'syncMaterial',
  'syncResourceNod',
] as const
export const SYNC_OPERATION_TYPES = createEnum(SYNC_OPERATION_TYPE_VALUES)
export type SyncOperationType = (typeof SYNC_OPERATION_TYPE_VALUES)[number]
